import React, { Component } from 'react';
import { Dimensions, LayoutAnimation, Modal, Platform, UIManager, View } from 'react-native';

import { isFunction as _isFunction } from 'lodash';
import PropTypes from 'prop-types';

import FoundationTestKeys from '../../FoundationTestKeys';
import { Icon } from '../Icon';
import { Images } from '../Themes';
import VFButton from '../VFButton';
import VFText from '../VFText';

import DIRECTIONS from './Directions';
import styles, { POINTER_SIZE } from './ToolTipsModal.Styles';

const SCREEN_HEIGHT = Dimensions.get('window').height;

class ToolTipModal extends Component {
  constructor(props) {
    super(props);
    this.state = {
      currentStep: 0,
      isVisible: false,
      positions: {},
      cardHeight: 0,
    };
  }

  componentDidMount = () => {
    if (Platform.OS === 'android' && UIManager.setLayoutAnimationEnabledExperimental) {
      UIManager.setLayoutAnimationEnabledExperimental(true);
    }
  };

  onPressNext = () => {
    const { currentStep } = this.state;
    const { steps } = this.props;
    if (_isFunction(steps[currentStep]?.onNext)) {
      steps[currentStep]?.onNext();
    }

    if (currentStep < steps.length - 1) {
      LayoutAnimation.configureNext(LayoutAnimation.Presets.easeInEaseOut);

      this.setState({
        currentStep: currentStep + 1,
      });
    } else {
      this.setState({
        currentStep: 0,
        isVisible: false,
      });
    }
  };

  onPressBack = () => {
    const { currentStep } = this.state;
    const { steps } = this.props;
    LayoutAnimation.configureNext(LayoutAnimation.Presets.easeInEaseOut);

    if (currentStep > 0) {
      if (_isFunction(steps[currentStep]?.onBack)) {
        steps[currentStep]?.onBack();
      }
      this.setState({
        currentStep: currentStep - 1,
      });
    }
  };

  onLayout = ({
    nativeEvent: {
      layout: { height },
    },
  }) => {
    this.setState({ cardHeight: height });
  };

  getStepPosition = steps => {
    const { positions, currentStep, cardHeight } = this.state;

    //terminate the function default values if below values are undefined
    if (!steps || !this.state.positions[steps[currentStep]?.id]) {
      return { isTop: true, vPosition: 0, hPosition: 0 };
    }

    // make card outside screen until it calculates its height
    if (cardHeight === 0) {
      return { isTop: true, vPosition: SCREEN_HEIGHT * 2, hPosition: 0 };
    }

    const { horizontalPercentage, verticalPercentage, direction } = steps[currentStep];
    const { x, y, width, height } = positions[steps[currentStep]?.id];

    // calculating pointer position on x-axis
    const hPosition = x + width * horizontalPercentage - POINTER_SIZE / 2;

    //calculating pointer position on y-axis
    const posAbove = y;
    const posBelow = y + height;

    /* determine whether tooltip box will appear above or below the component
     * according to the space above and below the component if its direction is not predefined
     */
    const isTop =
      direction === DIRECTIONS.UP
        ? true
        : direction === DIRECTIONS.DOWN
        ? false
        : posAbove > SCREEN_HEIGHT - posBelow;
    const vPosition = isTop
      ? posAbove - cardHeight + POINTER_SIZE * verticalPercentage
      : posBelow - POINTER_SIZE * verticalPercentage;

    return {
      isTop,
      vPosition,
      hPosition,
    };
  };

  // Called by a parent with ref to this component
  toggleModal = () => {
    const { steps } = this.props;
    const { isVisible } = this.state;
    if (steps?.length > 0) {
      this.setState({
        isVisible: !isVisible,
      });
    }
  };

  // Called by a parent with ref to this component
  updatePositions = positions => {
    this.setState({ positions });
  };
  renderTip = isTop => (
    <View style={styles.indicatorTip(isTop)} testID={FoundationTestKeys.toolTipsModalTip} />
  );

  renderIndicator = (isTop, hPosition) => {
    return (
      <View style={styles.indicator(hPosition)} testID={FoundationTestKeys.toolTipsModalIndicator}>
        {isTop && this.renderTip(isTop)}
        <Icon
          name={Images.toolTipIndicator}
          type="image"
          size={POINTER_SIZE}
          testID={FoundationTestKeys.toolTipsModalIndicatorIcon}
        />
        {!isTop && this.renderTip(isTop)}
      </View>
    );
  };
  renderCardContent = currentStep => {
    const { steps, title, buttonTitle, finishButtonTitle } = this.props;

    return (
      <View style={styles.content} testID={FoundationTestKeys.toolTipsModalContentView}>
        <View style={styles.titleContainer} testID={FoundationTestKeys.toolTipsModalTitleView}>
          <View
            style={styles.contentContainer}
            testID={FoundationTestKeys.toolTipsModalContentConatiner}
          >
            <VFText
              i18nKey={title}
              style={[styles.text, styles.titleText]}
              testKey={FoundationTestKeys.toolTipsModalTitleText}
            />
            <VFText
              numberOfLines={2}
              style={[styles.text, styles.contentText]}
              testKey={FoundationTestKeys.toolTipsModalContentText}
            >
              {steps[currentStep].content}
            </VFText>
          </View>

          <VFText style={styles.text} testKey={FoundationTestKeys.toolTipsModalStepsText}>
            {currentStep + 1}/{steps.length}
          </VFText>
        </View>

        <VFButton
          onPress={this.onPressNext}
          title={currentStep < steps.length - 1 ? buttonTitle : finishButtonTitle}
          type="quaternary"
          style={styles.nextButton}
          textStyle={styles.nextButtonText}
          testKey={FoundationTestKeys.toolTipsModalButton}
        />
      </View>
    );
  };
  render() {
    const { steps } = this.props;
    const { isVisible, currentStep, positions } = this.state;
    const { isTop, vPosition, hPosition } = this.getStepPosition(steps);
    const isCardVisible =
      steps?.length > 0 && positions[steps[currentStep].id]?.isLoading === false;
    return (
      <Modal
        onRequestClose={this.onPressBack}
        visible={isVisible}
        transparent
        testID={FoundationTestKeys.toolTipsModalModal}
      >
        {isCardVisible && (
          <View
            onLayout={this.onLayout}
            style={styles.container(vPosition)}
            testID={FoundationTestKeys.toolTipsModalView}
          >
            {!isTop && this.renderIndicator(isTop, hPosition)}
            {this.renderCardContent(currentStep)}
            {isTop && this.renderIndicator(isTop, hPosition)}
          </View>
        )}
      </Modal>
    );
  }
}
ToolTipModal.propTypes = {
  steps: PropTypes.array,
  title: PropTypes.string,
  buttonTitle: PropTypes.string,
  finishButtonTitle: PropTypes.string,
};

export default ToolTipModal;
